import { useContext, useMemo } from 'react';
import { LanguageContext } from '@/app/providers/LanguageContext';
import { translations } from './translations';

// Helper function to get nested value safely
const getNestedValue = (obj, keys) => {
  let value = obj;
  for (let i = 0; i < keys.length; i++) {
    if (!value || typeof value !== 'object') {
      return null;
    }
    // Support flat dotted keys like "status.present" alongside nested objects
    const dotted = keys.slice(i).join('.');
    if (Object.prototype.hasOwnProperty.call(value, dotted)) {
      return value[dotted];
    }
    if (keys[i] in value) {
      value = value[keys[i]];
    } else {
      return null;
    }
  }
  return value;
};

export const useTranslation = () => {
  const { language } = useContext(LanguageContext);
  
  const t = useMemo(() => {
    return (path) => {
      if (!path || typeof path !== 'string') {
        console.warn('Translation key must be a string:', path);
        return path || '';
      }

      const keys = path.split('.');
      
      // Try current language first
      const currentLangTranslations = translations[language] || translations.ar;
      let value = getNestedValue(currentLangTranslations, keys);
      
      // If found in current language, return it
      if (value && typeof value === 'string') {
        return value;
      }
      
      // Fallback to English (better default for non-Arabic locales)
      if (language !== 'en' && translations.en) {
        value = getNestedValue(translations.en, keys);
        if (value && typeof value === 'string') {
          console.warn(`Translation missing for "${path}" in ${language}, using English fallback`);
          return value;
        }
      }
      
      // Fallback to Arabic
      if (language !== 'ar' && translations.ar) {
        value = getNestedValue(translations.ar, keys);
        if (value && typeof value === 'string') {
          console.warn(`Translation missing for "${path}" in ${language}, using Arabic fallback`);
          return value;
        }
      }
      
      // Fallback to German
      if (language !== 'de' && translations.de) {
        value = getNestedValue(translations.de, keys);
        if (value && typeof value === 'string') {
          console.warn(`Translation missing for "${path}" in ${language}, using German fallback`);
          return value;
        }
      }
      
      // If still not found, log error and return path with warning
      console.error(`Translation key not found: "${path}"`);
      return `[MISSING: ${path}]`;
    };
  }, [language]);
  
  return { t, language };
};

